import React, {useState} from 'react';
import classNames from 'classnames';
import {useTranslation} from 'react-i18next';

import {ChevronLeftIcon, ChevronRightIcon} from '../SvgIcon';

import {DateRange, DateRangeComponentV2Props, PresetOption} from './types';
import {getDaysInMonth, getPresetRange, isDateInRange, isToday} from './utils';

const DateRangeComponentV2: React.FC<DateRangeComponentV2Props> = ({
  onChange,
  initialPreset = 'custom',
  initialRange,
  className,
  disabledDays = [],
}) => {
  const {t} = useTranslation();

  const [isOpen, setIsOpen] = useState(initialPreset === 'custom');
  const [selectedPreset, setSelectedPreset] =
    useState<PresetOption>(initialPreset);
  const [selectedRange, setSelectedRange] = useState<DateRange>(
    initialRange || {startDate: null, endDate: null},
  );
  const [tempRange, setTempRange] = useState<DateRange>(selectedRange);

  const [viewMonths, setViewMonths] = useState<[Date, Date]>(() => {
    const today = new Date();
    return [
      new Date(today.getFullYear(), today.getMonth(), 1),
      new Date(today.getFullYear(), today.getMonth() + 1, 1),
    ];
  });

  const handlePresetClick = (preset: PresetOption) => {
    setSelectedPreset(preset);

    if (preset === 'custom') {
      setIsOpen(true);
    } else {
      const newRange = getPresetRange(preset);
      setSelectedRange(newRange);
      setTempRange(newRange);
      onChange(newRange);
      setIsOpen(false);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setViewMonths(prevMonths => {
      const [firstMonth, secondMonth] = prevMonths;
      const increment = direction === 'prev' ? -1 : 1;

      return [
        new Date(
          firstMonth.getFullYear(),
          firstMonth.getMonth() + increment,
          1,
        ),
        new Date(
          secondMonth.getFullYear(),
          secondMonth.getMonth() + increment,
          1,
        ),
      ];
    });
  };

  const handleDateClick = (date: Date) => {
    if (!tempRange.startDate || (tempRange.startDate && tempRange.endDate)) {
      // Start new selection
      setTempRange({
        startDate: date,
        endDate: null,
      });
    } else {
      // Complete the selection
      const startDate = tempRange.startDate;

      if (date < startDate) {
        setTempRange({
          startDate: date,
          endDate: startDate,
        });
      } else {
        setTempRange({
          startDate,
          endDate: date,
        });
      }
    }
  };

  const handleApply = () => {
    if (tempRange.startDate && tempRange.endDate) {
      setSelectedRange(tempRange);
      onChange(tempRange);
    }
    setIsOpen(false);
  };

  const handleClose = () => {
    setTempRange(selectedRange);
    setIsOpen(false);
  };

  // Helper to check if a date is in any of the disabledDays ranges
  const isDateDisabled = (date: Date) => {
    if (!disabledDays || disabledDays.length === 0) return false;
    return disabledDays.some(range => {
      const {before, after} = range;
      // If both before and after are set, disable dates between (inclusive)
      if (before && after) {
        return date > after || date < before;
      }
      // If only before is set, disable all dates <= before
      if (before && !after) {
        return date < before;
      }
      // If only after is set, disable all dates >= after
      if (!before && after) {
        return date > after;
      }
      return false;
    });
  };

  // Helper function to get the appropriate class names for a date in the calendar
  const getDateClassNames = (date: Date) => {
    // Calculate state flags once
    const isFirstSelected = tempRange.startDate
      ? date.getTime() === tempRange.startDate.getTime()
      : false;
    const isLastSelected = tempRange.endDate
      ? date.getTime() === tempRange.endDate.getTime()
      : false;
    const isRangeEndpoint = isFirstSelected || isLastSelected;
    const isInRangeDate = isDateInRange(date, tempRange);
    const disabled = isDateDisabled(date);
    const isTodayDate = isToday(date);

    // Border radius class map
    return {
      isFirstSelected,
      isLastSelected,
      isInRangeDate,
      disabled,
      isTodayDate,
      className: classNames(
        // Base classes
        'aspect-square',
        'flex',
        'items-center',
        'justify-center',
        'border-none',
        'text-sm',
        'relative',
        'w-full',
        'p-1',
        'font-semibold',
        'opacity-100',

        // Background color classes
        {
          'bg-blue-700': isRangeEndpoint,
          'bg-blue-100': !isRangeEndpoint && isInRangeDate,
          'bg-transparent': !isRangeEndpoint && !isInRangeDate,
        },

        // Text color classes
        {
          'text-white': isRangeEndpoint,
          'text-gray-300': !isRangeEndpoint && disabled,
          'text-gray-900': !isRangeEndpoint && !disabled,
        },

        // Border radius classes
        {
          'rounded-lg': isFirstSelected && isLastSelected,
          'rounded-l-lg': isFirstSelected && !isLastSelected,
          'rounded-r-lg': !isFirstSelected && isLastSelected,
        },

        // Cursor classes
        {
          'cursor-default': disabled,
          'cursor-pointer': !disabled,
        },
      ),
    };
  };

  // Renders a single calendar month
  const renderCalendar = (month: Date) => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const days = getDaysInMonth(year, monthIndex);
    const weekdays = [
      t('weekdays.sun'),
      t('weekdays.mon'),
      t('weekdays.tue'),
      t('weekdays.wed'),
      t('weekdays.thu'),
      t('weekdays.fri'),
      t('weekdays.sat'),
    ];

    return (
      <div className="w-full">
        <div className="flex items-center justify-center pb-3">
          <h3 className="text-base font-medium m-0">
            {t('dateFormat.monthYear', {
              month: month.toLocaleDateString(undefined, {month: 'long'}),
              year: month.getFullYear(),
            })}
          </h3>
        </div>
        <div className="grid grid-cols-7 w-full gap-y-2">
          {weekdays.map(day => (
            <div key={day} className="text-center text-sm text-gray-500 py-2">
              {day}
            </div>
          ))}
          {days.map(({date, isCurrentMonth}) => {
            if (!isCurrentMonth) {
              // Render an empty cell for days not in the current month
              return (
                <div key={date.toISOString()} className="aspect-square"></div>
              );
            }
            const {disabled, className} = getDateClassNames(date);

            return (
              <button
                key={date.toISOString()}
                onClick={() => !disabled && handleDateClick(date)}
                disabled={disabled}
                className={className}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Helper function to render a preset button
  const renderPresetButton = (
    preset: PresetOption,
    labelKey: string,
    hasNoShadow = false,
  ) => {
    const isSelected = selectedPreset === preset;

    return (
      <button
        className={classNames(
          // Base classes
          'py-1',
          'px-2',
          'rounded-full',
          'text-sm',
          'font-medium',
          'transition-all',
          'whitespace-nowrap',

          // Conditional styling
          {
            // Selected state
            'bg-blue-700 text-white border-none hover:bg-blue-800': isSelected,

            // Unselected state
            'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50':
              !isSelected,
            'shadow-sm': !isSelected && !hasNoShadow,
          },
        )}
        onClick={() => handlePresetClick(preset)}
      >
        {t(labelKey)}
      </button>
    );
  };

  // Helper function to render a navigation button
  const renderNavButton = (
    direction: 'prev' | 'next',
    icon: React.ReactNode,
  ) => (
    <button
      className={classNames(
        'bg-transparent',
        'border-none',
        'cursor-pointer',
        'flex',
        'items-center',
        'justify-center',
        'p-1',
        'rounded-full',
        'hover:bg-gray-50',
      )}
      onClick={() => navigateMonth(direction)}
    >
      {icon}
    </button>
  );

  // Helper function to render an action button
  const renderActionButton = (
    label: string,
    onClick: () => void,
    isPrimary = false,
  ) => (
    <button
      className={classNames(
        // Base classes
        'py-2',
        'px-4',
        'rounded-full',
        'text-sm',
        'font-medium',

        // Conditional styling
        {
          // Primary button
          'bg-blue-700 text-white border-none hover:bg-blue-800': isPrimary,
          // Secondary button
          'bg-white text-gray-900 border border-gray-300 hover:bg-gray-50':
            !isPrimary,
        },
      )}
      onClick={onClick}
    >
      {t(label)}
    </button>
  );

  const presetButtons = [
    {preset: 'all_time', labelKey: 'dateRange.allTime', noShadow: true},
    {preset: '1_year', labelKey: 'dateRange.oneYear'},
    {preset: '6_months', labelKey: 'dateRange.sixMonths'},
    {preset: 'this_month', labelKey: 'dateRange.thisMonth'},
    {preset: 'custom', labelKey: 'dateRange.custom'},
  ];

  return (
    <div
      className={`
      font-sans bg-white rounded-lg
      shadow-md w-full max-w-5xl overflow-hidden
      w-[40rem]
      ${className}
    `}
    >
      <div className="flex gap-1 p-4 flex-wrap justify-start">
        {presetButtons.map(({preset, labelKey, noShadow}) => (
          <React.Fragment key={preset}>
            {renderPresetButton(preset as PresetOption, labelKey, noShadow)}
          </React.Fragment>
        ))}
      </div>

      {isOpen && (
        <>
          <div className="p-4 border-t border-gray-200 w-full flex justify-between items-start">
            {renderNavButton('prev', <ChevronLeftIcon />)}
            <div className="w-full">
              <div className="flex flex-wrap gap-3 justify-center w-full">
                <div className="w-full flex flex-col md:flex-row justify-center gap-2 md:gap-5">
                  {renderCalendar(viewMonths[0])}
                  {renderCalendar(viewMonths[1])}
                </div>
              </div>
            </div>
            {renderNavButton('next', <ChevronRightIcon />)}
          </div>
          <div className="flex justify-start p-4 gap-2 border-t border-gray-200">
            {renderActionButton('common.close', handleClose)}
            {renderActionButton('common.apply', handleApply, true)}
          </div>
        </>
      )}
    </div>
  );
};

export default DateRangeComponentV2;
